import React, { useEffect, useState } from "react";
import {
  ESTIMATE_TAX_TYPE_OPTIONS,
  applyTaxTypeSelection,
  formatStandardTaxRateDisplay,
  resolveTaxLineRate,
} from "../../../helpers/estimateTax";
import { formatCurrency } from "../../../helpers/estimateTotals";

function TaxSlot({ label, line, onChange, amount, disabled }) {
  const standard = line.type !== "custom";
  return (
    <div className="est-rcpt__tax">
      <span>{label}</span>
      <select aria-label={`${label} type`} value={line.type} disabled={disabled} onChange={(event) => onChange(applyTaxTypeSelection(line, event.target.value))}>
        {ESTIMATE_TAX_TYPE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <input
        aria-label={`${label} rate`}
        className="est-rcpt__input"
        inputMode="decimal"
        value={standard ? formatStandardTaxRateDisplay(line.type) : line.rate}
        readOnly={standard}
        disabled={disabled || standard}
        onChange={(event) => onChange({ ...line, rate: event.target.value })}
      />
      <span className="est-rcpt__amt">{formatCurrency(amount)}</span>
      {line.type === "custom" ? (
        <input
          aria-label={`${label} name`}
          className="est-rcpt__input est-rcpt__name"
          placeholder="Tax name"
          value={line.customName}
          disabled={disabled}
          onChange={(event) => onChange({ ...line, customName: event.target.value })}
        />
      ) : null}
    </div>
  );
}

function taxDollars(line, base) {
  if (!line || line.type === "none") return 0;
  const rate = resolveTaxLineRate(line);
  if (!rate || rate <= 0) return 0;
  return (Number(base) || 0) * rate / 100;
}

/** One receipt: estimate, extras, $ or % adjustment, subtotal, tax, total. */
export function PricingReceipt({
  disabled,
  subtotal,
  waste,
  travel,
  misc,
  onWaste,
  onTravel,
  onMisc,
  wasteTaxable = true,
  travelTaxable = true,
  miscTaxable = true,
  onWasteTaxable,
  onTravelTaxable,
  onMiscTaxable,
  adjustmentMode = "amount",
  adjustmentValue = "",
  onAdjustment,
  beforeTax,
  adjusted,
  taxBase,
  tax1,
  tax2,
  onTax1,
  onTax2,
  grandTotal,
  canSave,
  saving,
  onSave,
  schemaMissing,
}) {
  const [showTax2, setShowTax2] = useState(() => Boolean(tax2?.type && tax2.type !== "none"));
  useEffect(() => {
    if (tax2?.type && tax2.type !== "none") setShowTax2(true);
  }, [tax2?.type]);
  const runningSubtotal = beforeTax != null ? beforeTax : adjusted;
  const extras = [
    ["Waste", waste, onWaste, wasteTaxable, onWasteTaxable],
    ["Travel", travel, onTravel, travelTaxable, onTravelTaxable],
    ["Misc", misc, onMisc, miscTaxable, onMiscTaxable],
  ];

  return (
    <div className="est-rcpt" data-testid="pricing-receipt">
      {schemaMissing ? (
        <div className="est-d-warn">
          Pricing fields are not applied yet. Run migrations{" "}
          <code>20260625120000_estimate_pricing_summary.sql</code> and{" "}
          <code>20260629120000_estimate_tax_summary.sql</code> in Supabase.
        </div>
      ) : null}
      <div className="est-rcpt__row">
        <span>Subtotal</span>
        <span>{formatCurrency(subtotal)}</span>
      </div>
      {extras.map(([label, value, onChange, taxable, onTaxable]) => (
        <div key={label} className="est-rcpt__extra">
          <span>{label}</span>
          <label className="est-rcpt__taxcheck">
            <input
              type="checkbox"
              aria-label={`${label} taxable`}
              checked={taxable !== false}
              disabled={disabled}
              onChange={(event) => onTaxable?.(event.target.checked)}
            />
            Tax
          </label>
          <input
            aria-label={label}
            className="est-rcpt__input"
            inputMode="decimal"
            placeholder="0.00"
            value={value}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
      ))}
      <div className="est-rcpt__extra">
        <span>Adjustment</span>
        <div className="est-rcpt__switch" role="group" aria-label="Adjustment mode">
          <button type="button" aria-pressed={adjustmentMode !== "percent"} disabled={disabled} onClick={() => onAdjustment?.({ mode: "amount", value: adjustmentValue })}>$</button>
          <button type="button" aria-pressed={adjustmentMode === "percent"} disabled={disabled} onClick={() => onAdjustment?.({ mode: "percent", value: adjustmentValue })}>%</button>
        </div>
        <input
          aria-label="Subtotal adjustment"
          className="est-rcpt__input"
          inputMode="decimal"
          placeholder={adjustmentMode === "percent" ? "10 or -10" : "0.00"}
          value={adjustmentValue}
          disabled={disabled}
          onChange={(event) => onAdjustment?.({ mode: adjustmentMode || "amount", value: event.target.value })}
        />
      </div>
      <div className="est-rcpt__row is-mid">
        <span>Subtotal</span>
        <span>{formatCurrency(runningSubtotal)}</span>
      </div>
      <TaxSlot label="Tax 1" line={tax1} onChange={onTax1} amount={taxDollars(tax1, taxBase)} disabled={disabled} />
      {showTax2 ? (
        <div>
          <TaxSlot label="Tax 2" line={tax2} onChange={onTax2} amount={taxDollars(tax2, taxBase)} disabled={disabled} />
          {!disabled ? (
            <button type="button" className="eg-link" onClick={() => { onTax2(applyTaxTypeSelection(tax2, "none")); setShowTax2(false); }}>
              Remove tax 2
            </button>
          ) : null}
        </div>
      ) : (
        !disabled ? (
          <button type="button" className="eg-link" onClick={() => setShowTax2(true)}>Add tax 2</button>
        ) : null
      )}
      <div className="est-rcpt__row is-total">
        <span>Total</span>
        <span>{formatCurrency(grandTotal)}</span>
      </div>
      {canSave ? (
        <button type="button" className="dt-btn dt-btn--primary est-d-btn-block" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save Pricing"}
        </button>
      ) : null}
    </div>
  );
}
